import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PartyRows } from "@/components/PartyRows";

export const OWNER_KINDS = [["particular", "Particular"], ["empresa", "Empresa"], ["heranca", "Herança"]];
const TREATS = [["Sr.", "Sr."], ["Sra.", "Sra."], ["Dr.", "Dr."], ["Dra.", "Dra."], ["Eng.", "Eng."], ["Eng.ª", "Eng.ª"], ["none", "Sem tratamento"]];

export const Pills = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2" data-testid={prefix}>
    {options.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`px-3.5 py-1.5 rounded-lg border text-sm transition-colors ${value === k ? "bg-ec-green text-white border-ec-green" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
    ))}
  </div>
);

function Contact({ c, set, prefix, title }) {
  const up = (k) => (e) => set({ ...c, [k]: e.target.value });
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input placeholder="Nome" value={c.name || ""} onChange={up("name")} data-testid={`${prefix}-name`} />
        <Input placeholder="Telemóvel" value={c.phone || ""} onChange={up("phone")} inputMode="tel" data-testid={`${prefix}-phone`} />
        <Input type="email" placeholder="Email" value={c.email || ""} onChange={up("email")} data-testid={`${prefix}-email`} />
      </div>
      <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Tratar por</span>
        <Pills options={TREATS} value={c.treat || ""} onChange={(v) => set({ ...c, treat: v })} prefix={`${prefix}-treat`} /></div>
    </div>
  );
}

export function OwnerFields({ o, setO, sellers, setSellers, hideKind = false }) {
  const set = (k, v) => setO({ ...o, [k]: v });
  return (
    <div className="space-y-4">
      {!hideKind && <Pills options={OWNER_KINDS} value={o.kind} onChange={(v) => set("kind", v)} prefix="np-owner-kind" />}
      {o.kind === "particular" && <PartyRows list={sellers} setList={setSellers} prefix="np-seller" required civil={false} treat emailOptional={hideKind} addLabel={hideKind ? "outro proprietário" : undefined} />}
      {o.kind === "empresa" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2"><Label>Nome da empresa</Label><Input value={o.company || ""} onChange={(e) => set("company", e.target.value)} className="mt-1.5" placeholder="Ex.: Imobiliária Exemplo, Lda." data-testid="np-company-name" /></div>
            <div><Label>NIPC <span className="text-slate-400 font-normal">(opcional)</span></Label><Input value={o.nipc || ""} onChange={(e) => set("nipc", e.target.value)} className="mt-1.5" inputMode="numeric" data-testid="np-company-nipc" /></div>
          </div>
          <Contact c={o.contact || {}} set={(c) => set("contact", c)} prefix="np-company-contact" title="Quem trata do processo" />
          <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!o.statutesMinutes} onChange={(e) => set("statutesMinutes", e.target.checked)} data-testid="np-company-statutes-minutes" />Os estatutos exigem ata para vender</label>
        </div>
      )}
      {o.kind === "heranca" && (
        <div className="space-y-3">
          <Contact c={o.head || {}} set={(c) => set("head", c)} prefix="np-heir-head" title="Cabeça de casal" />
          <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!o.heirProxy} onChange={(e) => set("heirProxy", e.target.checked)} data-testid="np-heir-proxy" />Algum herdeiro faz-se representar (procuração)</label>
        </div>
      )}
    </div>
  );
}


export function ownerPayload(o, sellers) {
  if (o.kind === "empresa") {
    const c = o.contact || {};
    return { owner_kind: "empresa", has_company: true, sellers: [{ name: (o.company || "").trim(), nif: (o.nipc || "").trim() || null, representative: (c.name || "").trim(), rep_phone: c.phone, rep_email: (c.email || "").trim(), rep_title: c.treat || null, statutes_require_minutes: !!o.statutesMinutes }] };
  }
  if (o.kind === "heranca") {
    const h = o.head || {};
    return { owner_kind: "heranca", is_heranca: true, has_procuracao: !!o.heirProxy, sellers: [{ name: (h.name || "").trim(), phone: h.phone, email: (h.email || "").trim(), title: h.treat || null }] };
  }
  return { owner_kind: "particular", sellers: sellers.filter((s) => s.name.trim()) };
}

export function ownerError(o, sellers, emailOptional = false) {
  const noPhone = (p) => (p || "").replace(/\D/g, "").length < 9;
  const badMail = (e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((e || "").trim());
  const bad = (p, e) => noPhone(p) || badMail(e);
  if (o.kind === "empresa") {
    const c = o.contact || {};
    if (!(o.company || "").trim()) return "Indique o nome da empresa.";
    if ((c.name || "").trim().length < 3) return "Indique quem trata do processo pela empresa.";
    return bad(c.phone, c.email) ? `Indique o telemóvel e o email de ${c.name.trim()}.` : "";
  }
  if (o.kind === "heranca") {
    const h = o.head || {};
    if (!(h.name || "").trim()) return "Indique o cabeça de casal.";
    return bad(h.phone, h.email) ? `Indique o telemóvel e o email de ${h.name.trim()}.` : "";
  }
  const owners = sellers.filter((s) => s.name.trim());
  if (!owners.length) return "Indique o nome do proprietário.";
  const b = owners.find((s) => (emailOptional ? noPhone(s.phone) || ((s.email || "").trim() && badMail(s.email)) : bad(s.phone, s.email)));
  if (b) return emailOptional ? `Indique o telemóvel de ${b.name.trim()}${(b.email || "").trim() ? " e um email válido" : ""}.` : `Indique o telemóvel e o email de ${b.name.trim()}.`;
  const t = owners.find((s) => !["Sr.", "Sra."].includes(s.title));
  return t ? `Escolha Sr. ou Sra. para ${t.name.trim()}.` : "";
}
